import { BALANCE } from '../config/balance';
import type { RngStream } from '../core/rng';
import { normalize } from '../core/vec';
import type { EnemyKind, EnemyState } from '../core/types';
import { WEAPON_IDS } from './build';
import { updateBoss } from './boss';
import type { Engine } from './engine';

export interface Enemy {
  active: boolean;
  id: number;
  kind: EnemyKind;
  state: EnemyState;
  stateT: number;
  /** 生成弹入计时（0→spawnPopTime）。 */
  spawnT: number;
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  hp: number;
  maxHp: number;
  speed: number;
  damage: number;
  radius: number;
  dirX: number;
  dirY: number;
  kx: number;
  ky: number;
  flash: number;
  /** 远程怪开火计时。 */
  fireT: number;
  /** 精英词条 id。 */
  affixes: string[];
  /** 各武器命中冷却（顺序同 WEAPON_IDS）。 */
  hitCds: number[];
}

let nextId = 1;

export function createEnemy(): Enemy {
  return {
    active: false,
    id: nextId++,
    kind: 'chaser',
    state: 'approach',
    stateT: 0,
    spawnT: 0,
    x: 0, y: 0, prevX: 0, prevY: 0,
    hp: 1, maxHp: 1, speed: 0, damage: 0, radius: 10,
    dirX: 1, dirY: 0,
    kx: 0, ky: 0, flash: 0,
    fireT: 0, affixes: [],
    hitCds: WEAPON_IDS.map(() => 0),
  };
}

type AffixId = 'hp' | 'dmg' | 'speed' | 'size';
const AFFIX_IDS: AffixId[] = ['hp', 'dmg', 'speed', 'size'];

function rollAffixes(rng: RngStream, count: number): AffixId[] {
  return rng.shuffle([...AFFIX_IDS]).slice(0, count);
}

function applyAffix(e: Enemy, a: AffixId): void {
  const af = BALANCE.enemies.eliteAffixes;
  if (a === 'hp') e.maxHp *= af.hp;
  else if (a === 'dmg') e.damage *= af.dmg;
  else if (a === 'speed') e.speed *= af.speed;
  else e.radius *= af.size;
}

/** 生成初始化；普通 / 精英随时间缩放，Boss 固定数值。worldRng 仅精英词条使用。 */
export function initEnemy(
  e: Enemy,
  kind: EnemyKind,
  x: number,
  y: number,
  time: number,
  worldRng: RngStream,
): void {
  const b = BALANCE.enemies;
  const mins = time / 60;
  const hpMul = 1 + b.hpScalePerMin * mins;
  const dmgMul = 1 + b.dmgScalePerMin * mins;
  e.active = true;
  e.kind = kind;
  e.state = 'approach';
  e.stateT = 0;
  e.spawnT = 0;
  e.x = x; e.y = y;
  e.prevX = x; e.prevY = y;
  e.dirX = 1; e.dirY = 0;
  e.kx = 0; e.ky = 0;
  e.flash = 0;
  e.affixes.length = 0;
  for (let i = 0; i < e.hitCds.length; i++) e.hitCds[i] = 0;

  if (kind === 'chaser') {
    const c = b.chaser;
    e.maxHp = c.hp * hpMul;
    e.speed = c.speed;
    e.damage = c.damage * dmgMul;
    e.radius = c.radius;
  } else if (kind === 'charger') {
    const c = b.charger;
    e.maxHp = c.hp * hpMul;
    e.speed = c.speed;
    e.damage = c.damage * dmgMul;
    e.radius = c.radius;
  } else if (kind === 'bomber') {
    const c = b.bomber;
    e.maxHp = c.hp * hpMul;
    e.speed = c.speed;
    e.damage = c.damage * dmgMul;
    e.radius = c.radius;
  } else if (kind === 'spitter') {
    const c = b.spitter;
    e.maxHp = c.hp * hpMul;
    e.speed = c.speed;
    e.damage = c.damage * dmgMul;
    e.radius = c.radius;
    e.fireT = c.fireInterval;
    e.state = 'strafe';
  } else if (kind === 'elite') {
    const c = b.elite;
    e.maxHp = c.hp * hpMul;
    e.speed = c.speed;
    e.damage = c.damage * dmgMul;
    e.radius = c.radius;
    e.affixes = rollAffixes(worldRng, c.affixCount);
    for (const a of e.affixes) applyAffix(e, a as AffixId);
  } else {
    const c = kind === 'boss1' ? b.boss1 : b.boss2;
    e.maxHp = c.hp;
    e.speed = c.speed;
    e.damage = c.damage;
    e.radius = c.radius;
    e.state = 'approach';
  }
  e.hp = e.maxHp;
}

function moveToward(e: Enemy, x: number, y: number, speed: number, dt: number): void {
  const n = normalize(x - e.x, y - e.y);
  e.x += n.x * speed * dt;
  e.y += n.y * speed * dt;
}

function updateSpitter(e: Enemy, engine: Engine, dt: number): void {
  const c = BALANCE.enemies.spitter;
  const px = engine.player.x;
  const py = engine.player.y;
  const d = Math.hypot(px - e.x, py - e.y);
  if (d > c.keepDistance + 50) {
    moveToward(e, px, py, e.speed, dt);
  } else if (d < c.keepDistance - 50) {
    const n = normalize(e.x - px, e.y - py);
    e.x += n.x * e.speed * dt;
    e.y += n.y * e.speed * dt;
  } else {
    const side = e.id % 2 === 0 ? 1 : -1;
    const n = normalize(px - e.x, py - e.y);
    e.x += -n.y * e.speed * 0.7 * side * dt;
    e.y += n.x * e.speed * 0.7 * side * dt;
  }
  e.fireT -= dt;
  if (e.fireT <= 0) {
    e.fireT = c.fireInterval;
    engine.fireSpit(e);
  }
}

export function updateEnemy(e: Enemy, dt: number, engine: Engine): void {
  e.prevX = e.x;
  e.prevY = e.y;
  e.x += e.kx * dt;
  e.y += e.ky * dt;
  const kd = Math.exp(-BALANCE.weapons.boomerang.hitCd * dt);
  e.kx *= kd;
  e.ky *= kd;
  if (e.flash > 0) e.flash -= dt;
  for (let i = 0; i < e.hitCds.length; i++) {
    if (e.hitCds[i] > 0) e.hitCds[i] -= dt;
  }
  e.stateT += dt;
  if (e.spawnT < BALANCE.juice.spawnPopTime) e.spawnT += dt;

  if (e.kind === 'chaser' || e.kind === 'elite') {
    moveToward(e, engine.player.x, engine.player.y, e.speed, dt);
  } else if (e.kind === 'spitter') {
    updateSpitter(e, engine, dt);
  } else if (e.kind === 'bomber') {
    updateBomber(e, engine, dt);
  } else if (e.kind === 'boss1' || e.kind === 'boss2') {
    updateBoss(e, engine, dt);
  } else {
    updateCharger(e, engine, dt);
  }
}

function updateBomber(e: Enemy, engine: Engine, dt: number): void {
  if (e.state === 'fuse') return;
  moveToward(e, engine.player.x, engine.player.y, e.speed, dt);
  if (Math.hypot(engine.player.x - e.x, engine.player.y - e.y) <=
    BALANCE.enemies.bomber.armRadius) {
    e.state = 'fuse';
    e.stateT = 0;
  }
}

function updateCharger(e: Enemy, engine: Engine, dt: number): void {
  const c = BALANCE.enemies.charger;
  switch (e.state) {
    case 'approach':
      moveToward(e, engine.player.x, engine.player.y, e.speed, dt);
      if (Math.hypot(engine.player.x - e.x, engine.player.y - e.y) <= c.armRange) {
        const n = normalize(engine.player.x - e.x, engine.player.y - e.y);
        e.dirX = n.x; e.dirY = n.y;
        e.state = 'telegraph'; e.stateT = 0;
      }
      break;
    case 'telegraph':
      if (e.stateT >= c.telegraphTime) { e.state = 'charge'; e.stateT = 0; }
      break;
    case 'charge':
      e.x += e.dirX * c.chargeSpeed * dt;
      e.y += e.dirY * c.chargeSpeed * dt;
      if (e.stateT >= c.chargeTime) { e.state = 'tired'; e.stateT = 0; }
      break;
    case 'tired':
      if (e.stateT >= c.tiredTime) { e.state = 'approach'; e.stateT = 0; }
      break;
    default:
      e.state = 'approach'; e.stateT = 0;
  }
}
